import { createFileRoute, Link } from "@tanstack/react-router";
import { EMAIL, GBP, CallButton, TextButton } from "@/components/site-chrome";
import { RequestService } from "@/components/request-service";
import {
  Photo, imageSrc, imageSrcSet, Eyebrow, WeatherStrip, ServiceGrid, HowItWorks, ProblemStory, RealWork,
  WhyChoose, WhoWeServe, ReviewCard, ServiceAreaHours, FaqList, FinalCta,
} from "@/components/sections";
import { towns, reviews, homeFaqs, SITE } from "@/lib/site-data";
import { pageHead } from "@/lib/seo";

const HERO_WIDTHS = [640, 960, 1280, 1440] as const;
const HERO_SIZES = "100vw";

const TITLE = "Mobile Mechanic Great Falls MT | 406 Mobile Mechanic — We Come to You";
const DESC = "Mobile auto repair in Great Falls, MT — open 7 days, 8AM–9PM. Diagnostics, brakes, roadside help & more, done where you are. Call or text 406-799-0095.";

const businessLd = {
  "@context": "https://schema.org",
  "@type": "AutoRepair",
  name: "406 Mobile Mechanic",
  url: SITE,
  description: DESC,
  telephone: "+1-406-799-0095",
  email: EMAIL,
  foundingDate: "2020",
  founder: { "@type": "Person", name: "Josh Ginnaty" },
  areaServed: [...towns.map((t) => ({ "@type": "Place", name: `${t}, MT` })), { "@type": "State", name: "Montana" }],
  openingHoursSpecification: [{
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "08:00", closes: "21:00",
  }],
  sameAs: [GBP],
  review: Object.values(reviews).map((r) => ({ "@type": "Review", author: { "@type": "Person", name: r.name }, reviewBody: r.text })),
};

export const Route = createFileRoute("/")({
  head: () => {
    const h = pageHead("/", TITLE, DESC, [businessLd]);
    return {
      ...h,
      links: [...h.links, {
        rel: "preload",
        as: "image",
        href: imageSrc("hero", 960, "avif"),
        imageSrcSet: imageSrcSet("hero", HERO_WIDTHS, "avif"),
        imageSizes: HERO_SIZES,
        type: "image/avif",
        fetchPriority: "high",
      }],
    };
  },
  component: Index,
});

function Index() {
  return (
    <main>
      {/* 2. Hero */}
      <section className="relative cut-bottom overflow-hidden bg-charcoal-deep">
        <Photo
          name="hero"
          widths={HERO_WIDTHS}
          sizes={HERO_SIZES}
          alt="Customer's red sports car with the hood up getting repaired in falling snow, 406 Mobile Mechanic work truck parked behind"
          width={1440}
          height={1080}
          fetchPriority="high"
          pictureClassName="absolute inset-0"
          className="h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal-deep via-charcoal-deep/80 to-charcoal-deep/20" />
        <div className="relative mx-auto max-w-7xl px-4 pb-28 pt-14 sm:px-6 md:pb-44 md:pt-24">
          <p className="label-mono text-steel">Est. 2020 · Great Falls, MT · Open 7 Days</p>
          <h1 className="hero-title mt-5 max-w-3xl text-5xl font-black sm:text-7xl md:text-8xl">
            Great Falls' Mobile Mechanic — <span className="text-primary">We Come to You</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-foreground/90">
            Mobile auto repair at your home, job site, or roadside across Great Falls and Montana. Rain, sleet, snow, or sunshine — we get the job done.
          </p>
          <div className="hero-actions mt-8 flex flex-wrap gap-3">
            <CallButton label="Call Now" size="lg" />
            <TextButton label="Text Josh" size="lg" />
          </div>
          <p className="label-mono mt-8 text-foreground/75">Open 7 Days · 8AM–9PM · Est. 2020 · Fleet Service · Military Discounts</p>
        </div>
      </section>

      {/* 3. Ticker */}
      <div className="relative z-10 -mt-14"><WeatherStrip /></div>

      <ProblemStory />

      <section className="topo cut-both bg-charcoal-deep py-32" aria-labelledby="services-h">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Eyebrow>What we fix</Eyebrow>
          <h2 id="services-h" className="mt-4 max-w-3xl text-5xl font-black md:text-6xl">Mobile Auto Repair Services in Great Falls</h2>
          <ServiceGrid />
          <Link to="/services" className="label-mono mt-8 inline-block text-primary hover:underline">View all services →</Link>
        </div>
      </section>

      <HowItWorks />
      <RealWork />
      <WhyChoose />
      <WhoWeServe />

      <section className="bg-background py-24" aria-labelledby="reviews-h">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Eyebrow>Google reviews</Eyebrow>
          <h2 id="reviews-h" className="mt-4 text-5xl font-black md:text-6xl">What Montana Drivers Say About Josh</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <ReviewCard r={reviews.mccleary} featured />
            <ReviewCard r={reviews.cindy} />
            <ReviewCard r={reviews.deb} />
          </div>
          <div className="mt-10 text-center">
            <Link to="/reviews" className="inline-flex rounded-sm bg-primary px-8 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-primary-foreground transition-transform hover:-translate-y-0.5">
              Read More Reviews
            </Link>
          </div>
        </div>
      </section>

      <ServiceAreaHours />

      <section className="bg-background py-24" aria-labelledby="faq-h">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Eyebrow>Questions</Eyebrow>
          <h2 id="faq-h" className="mt-4 text-5xl font-black md:text-6xl">Mobile Mechanic FAQs</h2>
          <FaqList items={homeFaqs} />
          <Link to="/faq" className="label-mono mt-6 inline-block text-primary hover:underline">See all FAQs →</Link>
        </div>
      </section>

      <RequestService contactLink />
      <FinalCta />
    </main>
  );
}
